/**
 * Pure utility functions for workflow-graph persistence.
 * Extracted from WorkflowCanvas.jsx to enable unit testing (audit E3).
 *
 * UNIFIED MODEL: All nodes (steps AND forwarders) use step_id / next_step_id.
 * Forwarders are identified by their step_id, not by array index.
 *
 * EDGE RULE (2026-09-04): a non-condition node's next_step_id holds at most
 * ONE step target (form page, custom page, condition, redirect, end) plus any
 * number of forwarder targets. The step target is what the runtime follows;
 * forwarders are fired server-side. Condition nodes route via `branch` only.
 *
 * ENTRY POINT: `form.entry_step_id` records which step the Start node points
 * at. `undefined` (legacy document, never re-saved) falls back to steps[0];
 * `null` means the author deliberately disconnected Start.
 *
 * The Python mirror of normalizeWorkflowGraph lives in
 * backend/routes/forms.py (_normalize_workflow_graph). Keep them in sync.
 */

/**
 * Normalize next_step_id to an array.
 * Handles: string (legacy), array (new), undefined/null, missing key.
 */
export function getNextStepIds(step) {
    if (!step || !step.next_step_id) return [];
    return Array.isArray(step.next_step_id) ? step.next_step_id : [step.next_step_id];
}

/** True when `id` names a forwarder node (by config match, or the fwd_ prefix convention). */
export function isForwarderId(id, forwarders = []) {
    if (typeof id !== 'string' || !id) return false;
    if ((forwarders || []).some(f => f && f.step_id === id)) return true;
    return id.startsWith('fwd_');
}

/** The single step (non-forwarder) target of a node, or null. */
export function getStepTargetId(node, forwarders = []) {
    const ids = getNextStepIds(node);
    return ids.find(id => !isForwarderId(id, forwarders)) || null;
}

/** All forwarder targets of a node. */
export function getForwarderTargetIds(node, forwarders = []) {
    return getNextStepIds(node).filter(id => isForwarderId(id, forwarders));
}

/**
 * Add toStepId to the next_step_id array on the node matching fromStepId.
 * - A forwarder target is appended (deduplicated).
 * - A step target REPLACES any existing step target (one step target per node).
 * Non-condition steps only — condition nodes derive edges from branch config.
 * Works for both regular steps and forwarder configs (both use step_id).
 * @returns {{ items: Array, replacedStepId: string|null }} new array + the step id that was replaced (if any)
 */
export function replaceStepTarget(items, fromStepId, toStepId, forwarders = []) {
    if (!items || !fromStepId || !toStepId) return { items, replacedStepId: null };
    let replacedStepId = null;
    const next = items.map(s => {
        if (s.step_id !== fromStepId) return s;
        const existing = getNextStepIds(s);
        if (existing.includes(toStepId)) return s; // deduplicate
        if (isForwarderId(toStepId, forwarders)) {
            return { ...s, next_step_id: [...existing, toStepId] };
        }
        const prevStep = existing.find(id => !isForwarderId(id, forwarders)) || null;
        replacedStepId = prevStep;
        const fwdIds = existing.filter(id => isForwarderId(id, forwarders));
        return { ...s, next_step_id: [toStepId, ...fwdIds] };
    });
    return { items: next, replacedStepId };
}

/** Back-compat wrapper: returns just the new array. */
export function setStepNextStepId(steps, fromStepId, toStepId, forwarders = []) {
    return replaceStepTarget(steps, fromStepId, toStepId, forwarders).items;
}

/**
 * Remove toStepId from the next_step_id array on the node matching fromStepId.
 * If the array becomes empty, deletes the key entirely (not set to undefined).
 */
export function clearStepNextStepId(steps, fromStepId, toStepId) {
    if (!steps || !fromStepId || !toStepId) return steps;
    return steps.map(s => {
        if (s.step_id !== fromStepId) return s;
        const existing = getNextStepIds(s);
        if (existing.length === 0) return s;
        const filtered = existing.filter(id => id !== toStepId);
        const copy = { ...s };
        if (filtered.length === 0) {
            delete copy.next_step_id;
        } else {
            copy.next_step_id = filtered;
        }
        return copy;
    });
}

/**
 * Check if a node is a non-condition node (eligible for next_step_id persistence).
 */
export function isNonConditionNode(node) {
    if (!node || !node.stepId) return false;
    if (node.type?.startsWith('condition')) return false;
    return true;
}

/**
 * Determine whether a manual edge should be persisted via next_step_id.
 * Start-node edges are persisted separately (entry_step_id) — see isEntryEdge.
 */
export function shouldPersistManualEdge(fromNode, toNode) {
    if (!fromNode || !toNode) return false;
    if (!isNonConditionNode(fromNode)) return false;
    if (!toNode.stepId) return false;
    return true;
}

/** Ids that may not be the workflow entry: End steps and forwarders. */
export function isValidEntryTarget(stepId, steps, forwarders = []) {
    if (!stepId) return false;
    if (isForwarderId(stepId, forwarders)) return false;
    const step = (steps || []).find(s => s.step_id === stepId);
    return !!step && step.kind !== 'end';
}

/**
 * Resolve the entry step id for a form document.
 * undefined → legacy fallback to steps[0]; null → deliberately none.
 */
export function resolveEntryStepId(form) {
    if (!form) return null;
    if (form.entry_step_id === null) return null;
    if (form.entry_step_id) return form.entry_step_id;
    return form.steps?.[0]?.step_id || null;
}

/** Branch targets of a condition step (cases/branches targetStepId + default_target). */
export function getBranchTargetIds(step) {
    const b = step && step.branch;
    if (!b) return [];
    const out = [];
    (b.cases || []).forEach(c => { if (c && c.targetStepId) out.push(c.targetStepId); });
    (b.branches || []).forEach(br => { if (br && br.targetStepId) out.push(br.targetStepId); });
    if (b.default_target) out.push(b.default_target);
    return out;
}

function pruneBranch(branch, keep) {
    if (!branch) return branch;
    const next = { ...branch };
    if (next.cases) next.cases = next.cases.map(c => (c && c.targetStepId && !keep(c.targetStepId)) ? { ...c, targetStepId: '' } : c);
    if (next.branches) next.branches = next.branches.map(b => (b && b.targetStepId && !keep(b.targetStepId)) ? { ...b, targetStepId: '' } : b);
    if (next.default_target && !keep(next.default_target)) next.default_target = '';
    return next;
}

function pruneNextIds(node, keep, forwarders) {
    const ids = getNextStepIds(node).filter(keep);
    let stepSeen = false;
    const kept = ids.filter(id => {
        if (isForwarderId(id, forwarders)) return true;
        if (stepSeen) return false; // one step target per node
        stepSeen = true;
        return true;
    });
    // Keep the step target first, then forwarders (stable order among forwarders).
    const ordered = [...kept.filter(id => !isForwarderId(id, forwarders)), ...kept.filter(id => isForwarderId(id, forwarders))];
    const copy = { ...node };
    if (ordered.length === 0) delete copy.next_step_id;
    else copy.next_step_id = ordered;
    return copy;
}

/**
 * Normalize a form's workflow graph so the canvas, the runtime and the backend
 * agree on what is persisted:
 *   - drop references to ids that no longer exist (dangling after node deletion)
 *   - keep at most one step target per node (plus forwarders)
 *   - drop an entry_step_id that is missing, a forwarder, or an End step
 *   - heal legacy documents (entry_step_id undefined) to steps[0]
 * @returns {{ steps, forwarders, entry_step_id, changed: boolean }}
 */
export function normalizeWorkflowGraph(form) {
    const steps = form?.steps || [];
    const forwarders = form?.forwarders || [];
    const known = new Set([
        ...steps.map(s => s.step_id).filter(Boolean),
        ...forwarders.map(f => f.step_id).filter(Boolean),
    ]);
    const keep = (id) => known.has(id);

    const nextSteps = steps.map(s => {
        let out = pruneNextIds(s, keep, forwarders);
        if (out.branch) out = { ...out, branch: pruneBranch(out.branch, keep) };
        return out;
    });
    const nextForwarders = forwarders.map(f => {
        let out = pruneNextIds(f, keep, forwarders);
        if (out.correlation_source_step_id && !keep(out.correlation_source_step_id)) {
            out = { ...out, correlation_source_step_id: '' };
        }
        return out;
    });

    let entry = form?.entry_step_id;
    if (entry === undefined) entry = steps[0]?.step_id || null; // legacy heal
    if (entry && !isValidEntryTarget(entry, steps, forwarders)) entry = null;

    const changed = JSON.stringify({ a: steps, b: forwarders, c: form?.entry_step_id })
        !== JSON.stringify({ a: nextSteps, b: nextForwarders, c: entry });
    return { steps: nextSteps, forwarders: nextForwarders, entry_step_id: entry, changed };
}

/**
 * Remove every reference to a deleted node id: other nodes' next_step_id,
 * branch targets, forwarder correlation sources and the entry pointer.
 * The deleted node itself is removed from steps / forwarders.
 */
export function purgeNodeReferences(form, removedId) {
    const steps = (form?.steps || []).filter(s => s.step_id !== removedId);
    const forwarders = (form?.forwarders || []).filter(f => f.step_id !== removedId);
    const entry = form?.entry_step_id === removedId ? null : form?.entry_step_id;
    return normalizeWorkflowGraph({ ...form, steps, forwarders, entry_step_id: entry });
}

/**
 * Set of node ids (steps and forwarders) reachable from the entry step by
 * following next_step_id, forwarder next_step_id and every branch target.
 */
export function computeReachableIds(steps, forwarders = [], entryStepId) {
    const reachable = new Set();
    if (!entryStepId) return reachable;
    const byId = new Map();
    (steps || []).forEach(s => { if (s.step_id) byId.set(s.step_id, s); });
    (forwarders || []).forEach(f => { if (f.step_id) byId.set(f.step_id, f); });
    const stack = [entryStepId];
    while (stack.length) {
        const id = stack.pop();
        if (!id || reachable.has(id) || !byId.has(id)) continue;
        reachable.add(id);
        const node = byId.get(id);
        getNextStepIds(node).forEach(n => stack.push(n));
        getBranchTargetIds(node).forEach(n => stack.push(n));
    }
    return reachable;
}
